import { format } from "date-fns"
import { useEffect, useRef, useState } from "react"
import { IS_LOCALHOST } from "utils/constants"
import { trackingService } from "utils/tracking"
import { TrackingEvents } from "utils/tracking/events"

import { LearningActivityTrackerData } from "./useLearningActivityTracker"

const CHECK_INTERVAL = 1000

type ActivityTrackerOptions = {
  data?: LearningActivityTrackerData
  debug?: boolean
  activityTimeout: number // in milliseconds, default is 1 minute
  enabled: boolean
  id: string | number
}

const DEFAULT_ACTIVITY_TIMEOUT = 1 * 60 * 1000 // 1 minute

// localStorage can be null (e.g. iOS Google app) or throw when storage is blocked
export const isLocalStorageFlagSet = (key: string) => {
  try {
    return window.localStorage?.getItem(key) === "1"
  } catch {
    return false
  }
}

const debugTimeout = isLocalStorageFlagSet("short_activity_timeout")

export function useUserActivityTracker({
  id,
  data,
  debug = false,
  enabled,
  activityTimeout: ACTIVITY_TIMEOUT = DEFAULT_ACTIVITY_TIMEOUT,
}: ActivityTrackerOptions) {
  ACTIVITY_TIMEOUT = debugTimeout ? DEFAULT_ACTIVITY_TIMEOUT : ACTIVITY_TIMEOUT // 1 minute by default, can be overridden by localStorage debug setting

  const [isActive, setIsActive] = useState(true)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [lastPing, setLastPing] = useState<Date | null>(null)
  const [now] = useState(() => Date.now())
  const lastActivityRef = useRef(now)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const nextPingRef = useRef<number | null>(null)
  const dataRef = useRef(data)

  const isActiveRef = useRef(isActive)
  const lastPingRef = useRef<Date | null>(null)

  const markActivity = () => {
    lastActivityRef.current = Date.now()
  }

  const sendActivityPing = async () => {
    const now = new Date()
    if (!dataRef.current?.user_planner_item_id || IS_LOCALHOST) {
      console.log(`> Skipping activity ping`, dataRef.current)
      return
    }
    console.log(`> Sending activity ping`, dataRef.current)
    trackingService.trackEvent(
      TrackingEvents.userLearningActivity,
      dataRef.current
    )
    setLastPing(now)
    lastPingRef.current = now
  }

  useEffect(() => {
    dataRef.current = data
  }, [data])

  useEffect(() => {
    isActiveRef.current = isActive
  }, [isActive])

  // Activity detection loop
  useEffect(() => {
    if (!enabled) return

    const check = setInterval(() => {
      const now = Date.now()
      const inactiveFor = now - lastActivityRef.current
      const currentlyActive = inactiveFor < ACTIVITY_TIMEOUT

      if (currentlyActive !== isActiveRef.current) {
        setIsActive(currentlyActive)
        isActiveRef.current = currentlyActive
      }
    }, CHECK_INTERVAL)

    return () => clearInterval(check)
  }, [enabled, ACTIVITY_TIMEOUT, id])

  // Tracking Activity
  useEffect(() => {
    if (!enabled) return

    if (isActive) {
      sendActivityPing()
      const interval = setInterval(() => {
        sendActivityPing()
        nextPingRef.current = Date.now() + ACTIVITY_TIMEOUT
      }, ACTIVITY_TIMEOUT)
      intervalRef.current = interval
      nextPingRef.current = Date.now() + ACTIVITY_TIMEOUT
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
      nextPingRef.current = null
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [isActive, enabled, ACTIVITY_TIMEOUT, id])

  // Activity event listeners
  useEffect(() => {
    if (!enabled) return

    const windowEvents = ["mousemove", "scroll", "keydown", "visibilitychange"]
    const videoEvents = ["play", "playing", "timeupdate"]

    const handler = (e: Event) => {
      // if (debug) console.log(`> Activity detected: ${e.type}`)
      if (
        e.type === "visibilitychange" &&
        document.visibilityState === "visible"
      ) {
        markActivity()
      } else if (e.type !== "visibilitychange") {
        markActivity()
      }
    }

    // Attach window events
    windowEvents.forEach(event => window.addEventListener(event, handler))

    // Attach video events to all video elements
    const videos = Array.from(document.getElementsByTagName("video"))
    videos.forEach(video => {
      videoEvents.forEach(event => video.addEventListener(event, handler))
    })

    // MutationObserver to handle dynamically added/removed videos
    // const observer = new MutationObserver(() => {
    //   const currentVideos = Array.from(document.getElementsByTagName("video"))
    //   currentVideos.forEach(video => {
    //     videoEvents.forEach(event => {
    //       if (!video[`__activity_${event}`]) {
    //         video.addEventListener(event, handler)
    //         video[`__activity_${event}`] = true
    //       }
    //     })
    //   })
    // })
    // observer.observe(document.body, { childList: true, subtree: true })

    return () => {
      windowEvents.forEach(event => window.removeEventListener(event, handler))
      const allVideos = Array.from(document.getElementsByTagName("video"))
      allVideos.forEach(video => {
        videoEvents.forEach(event => video.removeEventListener(event, handler))
      })
      // observer.disconnect()
    }
  }, [enabled, id])

  // ✅ Debug UI (fixed to use refs)
  useEffect(() => {
    if (!debug || !enabled) return

    const id = "user-activity-debug"
    let container = document.getElementById(id)
    if (!container) {
      container = document.createElement("div")
      container.id = id
      container.style.cssText = `
        position: fixed;
        bottom: 16px;
        right: 16px;
        background: rgba(0,0,0,0.85);
        color: white;
        padding: 12px 16px;
        border-radius: 8px;
        font-size: 12px;
        z-index: 9999;
        font-family: monospace;
        line-height: 1.4;
        max-width: 220px;
      `
      document.body.appendChild(container)
    }

    const updateUI = () => {
      const now = Date.now()
      const inactiveIn = Math.max(
        0,
        Math.floor((ACTIVITY_TIMEOUT - (now - lastActivityRef.current)) / 1000)
      )

      const nextPingIn = nextPingRef.current
        ? Math.max(0, Math.floor((nextPingRef.current - now) / 1000))
        : "N/A"

      container.innerHTML = `
        <div><b>User is active:</b> ${
          isActiveRef.current ? "✅ Yes" : "❌ No"
        }</div>
        <div><b>Inactive in:</b> ${inactiveIn}s</div>
        <div><b>Next ping in:</b> ${nextPingIn}s</div>
        <div><b>Last ping:</b> ${
          lastPingRef.current
            ? format(lastPingRef.current, "hh:mm:ss a")
            : "N/A"
        }</div>
        <b>Activity timeout:</b> ${ACTIVITY_TIMEOUT / 1000 / 60}m</div>
      `
    }

    const interval = setInterval(updateUI, 1000)
    return () => {
      clearInterval(interval)
      container?.remove()
    }
  }, [debug, enabled, ACTIVITY_TIMEOUT, id])
}
